// 登录页:用户名 + 密码(fenxi 管理员登录)
// 公众号扫码登录走 Settings → 公众号 Cookie → 「开始扫码」入口(CLAUDE.md §6 第一条允许的 Playwright 场景)
// 品牌头简化为微信绿方块 + 「fenxi」小字;Card 1px border、4px 圆角
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Alert, Button, Form, Input, App } from 'antd'
import { LogIn, ShieldCheck, KeyRound, User, BarChart2 } from 'lucide-react'
import { post } from '@/lib/api'
import { setToken, isAuthed } from '@/lib/auth'

interface LoginResponse {
  token: string
  username: string
  role: string
}

interface LocationState {
  from?: string
}

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { message } = App.useApp()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const from = (location.state as LocationState | null)?.from || '/'

  useEffect(() => {
    if (isAuthed()) navigate(from, { replace: true })
  }, [from, navigate])

  async function onSubmit(values: { username: string; password: string }) {
    setError(null)
    setSubmitting(true)
    try {
      const resp = await post<LoginResponse>('/auth/login', values)
      setToken(resp.token, resp.username, resp.role)
      message.success(`欢迎回来,${resp.username}`)
      navigate(from, { replace: true })
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data
        ?.error?.message
      setError(detail || '登录失败:用户名或密码错误')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-ink-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* 品牌头 — 微信绿方块 + fenxi */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="w-8 h-8 rounded bg-primary-500 flex items-center justify-center text-white">
            <BarChart2 size={16} />
          </div>
          <h1 className="text-xl font-semibold text-ink-900">fenxi</h1>
        </div>

        {/* 登录卡片 — 1px wxgray border + 4px 圆角(用 card utility) */}
        <div className="card">
          <h2 className="text-lg font-semibold text-ink-900 mb-1 flex items-center gap-2">
            <LogIn size={18} className="text-primary-600" />
            管理员登录
          </h2>
          <p className="text-sm text-ink-500 mb-5">单用户场景,默认账号见 .env / AUTH_USER</p>

          {error && (
            <Alert
              type="error"
              showIcon
              message={error}
              className="!mb-4"
              closable
              onClose={() => setError(null)}
            />
          )}

          <Form
            layout="vertical"
            requiredMark={false}
            initialValues={{ username: '', password: '' }}
            onFinish={onSubmit}
            autoComplete="off"
          >
            <Form.Item
              name="username"
              label="用户名"
              rules={[{ required: true, message: '请输入用户名' }]}
            >
              <Input
                size="large"
                placeholder="admin"
                prefix={<User size={16} className="text-ink-400" />}
                autoFocus
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password
                size="large"
                placeholder="••••••••"
                prefix={<KeyRound size={16} className="text-ink-400" />}
              />
            </Form.Item>

            <Form.Item className="!mb-0">
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
              >
                登录
              </Button>
            </Form.Item>
          </Form>

          <div className="mt-5 pt-4 border-t border-ink-100 flex items-start gap-2 text-xs text-ink-500">
            <ShieldCheck size={14} className="text-primary-600 shrink-0 mt-0.5" />
            <span className="leading-relaxed">
              凭据通过 HTTPBasic 校验,token 用 base64(user:pass) 编码后存
              localStorage;公众号 Cookie 在登录后到「设置 → 公众号 Cookie」手动粘贴。
            </span>
          </div>
        </div>

        <p className="text-center text-xs text-ink-400 mt-6">
          fenxi v0.1 · 单用户工具 · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  )
}
